import * as React from 'react';
import {
  Building,
  Building2,
  HeartPulse,
  LayoutDashboard,
  ListTree,
  Megaphone,
  ScrollText,
  Shield,
} from 'lucide-react';

import { AdminNavMain } from './admin-nav-main';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '../ui/sidebar';
import { Link } from 'react-router';
import { routes } from '@/routing';
import { useAdminAuth } from '@/hooks/use-admin-auth';
import { normalizeAdminRole } from '@/services/admin-auth';

export const AdminAppSidebar: React.FC = ({
  ...props
}: React.ComponentProps<typeof Sidebar>) => {
  const { admin } = useAdminAuth();

  const roleLabel = admin?.role ? normalizeAdminRole(admin.role) : 'Admin';
  const adminLabel = `Admin (${roleLabel})`;

  const data = React.useMemo(
    () => ({
      navMain: [
        {
          title: 'Dashboard',
          url: routes.adminDashboard,
          icon: LayoutDashboard,
          isActive: true,
        },
        {
          title: 'Tenants',
          url: routes.adminTenants,
          icon: Building2,
        },
        {
          title: 'Businesses',
          url: routes.adminBusinesses,
          icon: Building,
        },
        {
          title: 'Campaigns',
          url: routes.adminCampaigns,
          icon: Megaphone,
        },
        {
          title: 'Reference Data',
          url: routes.adminReferenceData,
          icon: ListTree,
        },
        {
          title: 'Audit Logs',
          url: routes.adminAuditLogs,
          icon: ScrollText,
        },
        {
          title: 'Payments Health',
          url: routes.adminPaymentsHealth,
          icon: HeartPulse,
        },
      ],
    }),
    []
  );

  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      className="w-64 bg-admin-secondary border-r border-admin-border p-0"
      {...props}
    >
      <SidebarHeader className="border-b border-admin-border p-4 bg-admin-secondary-light">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="hover:bg-admin-primary group-data-[collapsible=icon]:justify-center"
              tooltip={adminLabel}
            >
              <Link
                to={routes.adminDashboard}
                className="flex items-center gap-3 focus-visible:bg-transparent"
              >
                <div className="w-8 h-8 rounded-lg bg-admin-primary grid place-items-center shrink-0">
                  <Shield className="w-5 h-5 text-admin-text" />
                </div>
                <span className="text-sm font-semibold text-admin-text uppercase tracking-wider group-data-[collapsible=icon]:hidden">
                  {roleLabel}
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="flex-1 overflow-y-auto py-4 pl-2">
        <AdminNavMain items={data.navMain} label={adminLabel} />
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
};
